import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Sparkles, Check, X } from 'lucide-react';
import { api } from '../../lib/api';

export function AutoJournalPage() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['draft-journals'],
    queryFn: () => api.get<{ data: any[] }>('/api/bank/draft-journals'),
  });

  const approveMutation = useMutation({
    mutationFn: (id: string) => api.post(`/api/bank/draft-journals/${id}/approve`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['draft-journals'] });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (id: string) => api.post(`/api/bank/draft-journals/${id}/reject`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['draft-journals'] });
    },
  });

  const processMutation = useMutation({
    mutationFn: () => api.post('/api/ai/process-transactions'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['draft-journals'] });
    },
  });

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
            自動仕訳連携センター
          </h1>
          <p style={{ color: 'var(--text-secondary)' }}>
            AI提案の仕訳を確認・承認してください
          </p>
        </div>
        <button
          onClick={() => processMutation.mutate()}
          disabled={processMutation.isPending}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-white font-medium"
          style={{ backgroundColor: 'var(--accent-color)' }}
        >
          <Sparkles size={18} />
          {processMutation.isPending ? 'AI処理中...' : 'AI自動仕訳実行'}
        </button>
      </div>

      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
          </div>
        ) : data?.data.length === 0 ? (
          <div className="text-center py-12">
            <Sparkles size={48} className="mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
            <p style={{ color: 'var(--text-secondary)' }}>処理待ちの仕訳提案はありません</p>
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
            {data?.data.map((draft: any) => (
              <div key={draft.id} className="p-4 flex items-center gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono text-sm" style={{ color: 'var(--text-primary)' }}>
                      {draft.transactionDate}
                    </span>
                    <span
                      className="px-2 py-0.5 rounded text-xs"
                      style={{
                        backgroundColor: draft.confidenceScore > 0.8 ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                        color: draft.confidenceScore > 0.8 ? 'var(--success)' : 'var(--warning)',
                      }}
                    >
                      確信度: {(draft.confidenceScore * 100).toFixed(0)}%
                    </span>
                  </div>
                  <p style={{ color: 'var(--text-primary)' }}>{draft.description}</p>
                  <div className="flex items-center gap-4 mt-2 text-sm">
                    <span style={{ color: 'var(--text-secondary)' }}>
                      金額: <strong className="font-mono">¥{parseFloat(draft.amount).toLocaleString()}</strong>
                    </span>
                    <span style={{ color: 'var(--text-secondary)' }}>
                      推奨科目: <strong>{draft.suggestedAccountCode} {draft.suggestedAccountName}</strong>
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => approveMutation.mutate(draft.id)}
                    disabled={approveMutation.isPending}
                    className="p-2 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-600 hover:bg-green-100"
                    title="承認"
                  >
                    <Check size={20} />
                  </button>
                  <button
                    onClick={() => rejectMutation.mutate(draft.id)}
                    disabled={rejectMutation.isPending}
                    className="p-2 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 hover:bg-red-100"
                    title="却下"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

